/**
 * ProjectContextPanel - Collapsible sidebar showing project context
 * 
 * Displays:
 * - Project name & icon
 * - Connected apps
 * - Running tasks
 * - Knowledge files & links
 * 
 * Builds user trust by showing what SuperJarvis knows about their project.
 */
import React, { useState } from 'react';
import {
  ChevronRight,
  ChevronLeft,
  Link,
  FileText,
  CheckSquare,
  Grid,
  Plus,
  X
} from './shared';

// =============================================================================
// TYPES
// =============================================================================

interface ProjectApp {
  id?: string;
  provider: string;
  name: string;
  icon: string | React.ReactNode;  // Can be emoji string or React component
  connected_at?: string;
}

interface ProjectTask {
  id: string;
  title: string;
  status: 'running' | 'pending' | 'scheduled' | 'completed';
}

interface ProjectKnowledge {
  files: string[];
  links: string[];
}

interface ProjectContextPanelProps {
  projectId: string;
  projectName: string;
  projectIcon: string;
  projectColor: string;
  apps?: ProjectApp[];
  tasks?: ProjectTask[];
  knowledge?: ProjectKnowledge;
  tasksCount?: number;
  onAddApp?: () => void;
  onAddFile?: () => void;
  onAddLink?: () => void;
  onManageApps?: () => void;
  onManageTasks?: () => void;
  onManageKnowledge?: () => void;
  isOpen?: boolean;
  onToggle?: () => void;
}

// =============================================================================
// STATIC APP ICONS (for connected apps - same as MyAppsScreen)
// =============================================================================

const GoogleCalendarIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
    <rect x="4" y="4" width="16" height="16" rx="2" fill="#4285F4"/>
    <path d="M8 2v4M16 2v4M4 10h16" stroke="white" strokeWidth="2"/>
  </svg>
);

const GmailIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
    <rect x="2" y="4" width="20" height="16" rx="2" fill="#EA4335"/>
    <path d="M2 6l10 7 10-7" stroke="white" strokeWidth="2"/>
  </svg>
);

const SlackIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
    <path d="M6 15a2 2 0 104 0v-5H6a2 2 0 000 4z" fill="#E01E5A"/>
    <path d="M18 9a2 2 0 10-4 0v5h4a2 2 0 000-4z" fill="#36C5F0"/>
    <path d="M9 18a2 2 0 100-4H4a2 2 0 000 4z" fill="#2EB67D"/>
    <path d="M15 6a2 2 0 100 4h5a2 2 0 000-4z" fill="#ECB22E"/>
  </svg>
);

// Static connected apps (fallback when no apps are connected)
const DEFAULT_CONNECTED_APPS: ProjectApp[] = [
  { provider: 'google_calendar', name: 'Google Calendar', icon: <GoogleCalendarIcon /> },
  { provider: 'gmail', name: 'Gmail', icon: <GmailIcon /> },
  { provider: 'slack', name: 'Slack', icon: <SlackIcon /> },
];

// =============================================================================
// SUBCOMPONENTS
// =============================================================================

const SectionHeader = ({ 
  icon: Icon, 
  title, 
  count,
  onAction,
  actionLabel = 'Manage'
}: { 
  icon: React.ElementType;
  title: string;
  count?: number;
  onAction?: () => void;
  actionLabel?: string;
}) => (
  <div className="flex items-center justify-between mb-2">
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-cyan-400" />
      <span className="text-xs font-bold text-cyan-400/70 uppercase tracking-wider">
        {title}
      </span>
      {count !== undefined && (
        <span className="text-xs text-cyan-400/50">({count})</span>
      )}
    </div>
    {onAction && (
      <button 
        onClick={onAction}
        className="text-[10px] text-cyan-400/50 hover:text-cyan-300 transition-colors"
      >
        {actionLabel}
      </button>
    )}
  </div>
);

const AppItem = ({ app }: { app: ProjectApp }) => (
  <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-cyan-400/5 transition-colors">
    <div className="w-6 h-6 rounded-lg bg-cyan-400/10 flex items-center justify-center shrink-0">
      {typeof app.icon === 'string' ? (
        <span className="text-sm">{app.icon}</span>
      ) : (
        app.icon
      )}
    </div>
    <span className="text-sm text-cyan-300/80 truncate">{app.name}</span>
  </div>
);

const TaskItem = ({ task }: { task: ProjectTask }) => {
  const statusColors = {
    running: 'bg-green-400',
    pending: 'bg-yellow-400',
    scheduled: 'bg-blue-400',
    completed: 'bg-cyan-400/50'
  };
  
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-cyan-400/5 transition-colors">
      <div className={`w-2 h-2 rounded-full ${statusColors[task.status]} shrink-0`} />
      <span className="text-sm text-cyan-300/80 truncate">{task.title}</span>
    </div>
  );
};

const KnowledgeItem = ({ 
  item, 
  type,
  onRemove
}: { 
  item: string; 
  type: 'file' | 'link';
  onRemove?: () => void;
}) => {
  const Icon = type === 'file' ? FileText : Link;
  const displayName = type === 'link' 
    ? (() => {
        try {
          return new URL(item).hostname.replace('www.', '');
        } catch {
          return item;
        }
      })()
    : item;
  
  return (
    <div className="group flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-cyan-400/5 transition-colors">
      <Icon className="w-4 h-4 text-cyan-400/60 shrink-0" />
      <span className="text-sm text-cyan-300/80 truncate flex-1">{displayName}</span>
      {onRemove && (
        <button 
          onClick={onRemove}
          className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-cyan-400/10 transition-all"
        >
          <X className="w-3 h-3 text-cyan-400/50" />
        </button>
      )}
    </div>
  );
};

const AddButton = ({ onClick, label }: { onClick: () => void; label: string }) => (
  <button 
    onClick={onClick}
    className="flex items-center gap-1.5 px-2 py-1 text-xs text-cyan-400/50 hover:text-cyan-300 hover:bg-cyan-400/5 rounded-lg transition-colors"
  >
    <Plus className="w-3 h-3" />
    <span>{label}</span>
  </button>
);

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const ProjectContextPanel: React.FC<ProjectContextPanelProps> = ({
  projectName,
  projectIcon,
  projectColor,
  apps = DEFAULT_CONNECTED_APPS,
  tasks = [],
  knowledge = { files: [], links: [] },
  tasksCount = 0,
  onAddApp,
  onAddFile,
  onAddLink,
  onManageApps,
  onManageTasks,
  onManageKnowledge,
  isOpen = true,
  onToggle
}) => {
  // Internal state for when parent doesn't control open state
  const [internalOpen, setInternalOpen] = useState(isOpen);
  const panelOpen = onToggle ? isOpen : internalOpen;
  const togglePanel = onToggle || (() => setInternalOpen(!internalOpen));

  return (
    <div 
      className={`fixed top-20 right-4 bottom-20 z-40 transition-all duration-300 ease-out ${
        panelOpen 
          ? 'w-72 opacity-100 translate-x-0' 
          : 'w-10 opacity-90 translate-x-0'
      }`}
    >
      {/* Toggle Button */}
      <button
        onClick={togglePanel}
        className={`absolute top-4 ${panelOpen ? '-left-4' : 'left-0'} w-8 h-8 rounded-full bg-black/90 border border-cyan-400/30 flex items-center justify-center hover:bg-cyan-400/10 hover:border-cyan-400/50 transition-all z-50 shadow-[0_0_15px_rgba(34,211,238,0.2)]`}
      >
        {panelOpen ? (
          <ChevronRight className="w-4 h-4 text-cyan-400" />
        ) : (
          <ChevronLeft className="w-4 h-4 text-cyan-400" />
        )}
      </button>

      {/* Panel Content */}
      {panelOpen && (
        <div className="h-full bg-black/95 backdrop-blur-xl border border-cyan-400/20 rounded-2xl shadow-[0_0_40px_rgba(34,211,238,0.15)] overflow-hidden flex flex-col">
          {/* Header - Project Info */}
          <div className="p-4 border-b border-cyan-400/10">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${projectColor} flex items-center justify-center shadow-lg`}>
                <span className="text-lg">{projectIcon}</span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-cyan-300 font-semibold text-sm truncate">{projectName}</h3>
                <p className="text-cyan-400/50 text-xs">Project Context</p>
              </div>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-5">
            {/* Connected Apps */}
            <div>
              <SectionHeader 
                icon={Grid} 
                title="Apps" 
                count={apps.length}
                onAction={onManageApps}
              />
              <div className="space-y-1">
                {apps.map(app => (
                  <AppItem key={app.provider} app={app} />
                ))}
                {onAddApp && (
                  <AddButton onClick={onAddApp} label="Connect app" />
                )}
              </div>
            </div>

            {/* Tasks */}
            <div>
              <SectionHeader 
                icon={CheckSquare} 
                title="Tasks" 
                count={tasks.length || tasksCount}
                onAction={onManageTasks}
              />
              <div className="space-y-1">
                {tasks.length > 0 ? (
                  <>
                    {tasks.slice(0, 5).map(task => (
                      <TaskItem key={task.id} task={task} />
                    ))}
                    {tasks.length > 5 && (
                      <p className="text-xs text-cyan-400/40 px-2 py-1">
                        +{tasks.length - 5} more tasks
                      </p>
                    )}
                  </>
                ) : tasksCount > 0 ? (
                  <p className="text-xs text-cyan-400/40 px-2 py-1">
                    {tasksCount} task{tasksCount !== 1 ? 's' : ''} in this project
                  </p>
                ) : (
                  <p className="text-xs text-cyan-400/40 px-2 py-1">
                    No tasks yet
                  </p>
                )}
              </div>
            </div>

            {/* Knowledge */}
            <div>
              <SectionHeader 
                icon={FileText} 
                title="Knowledge" 
                count={(knowledge.files?.length || 0) + (knowledge.links?.length || 0)}
                onAction={onManageKnowledge}
              />
              <div className="space-y-1">
                {/* Files */}
                {knowledge.files?.map((file, i) => (
                  <KnowledgeItem key={`file-${i}`} item={file} type="file" />
                ))}
                
                {/* Links */}
                {knowledge.links?.map((link, i) => (
                  <KnowledgeItem key={`link-${i}`} item={link} type="link" />
                ))}
                
                {/* Empty state */}
                {(!knowledge.files?.length && !knowledge.links?.length) && (
                  <p className="text-xs text-cyan-400/40 px-2 py-1">
                    No knowledge added yet
                  </p>
                )}
                
                {/* Add buttons */}
                <div className="flex gap-2 pt-1">
                  {onAddFile && (
                    <AddButton onClick={onAddFile} label="Upload" />
                  )}
                  {onAddLink && (
                    <AddButton onClick={onAddLink} label="Add link" />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-cyan-400/10">
            <p className="text-[10px] text-cyan-400/30 text-center">
              SuperJarvis uses this context for your project
            </p>
          </div>
        </div>
      )}

      {/* Collapsed State - Mini indicator */}
      {!panelOpen && (
        <div className="h-full flex flex-col items-center pt-16 space-y-4">
          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${projectColor} flex items-center justify-center`}>
            <span className="text-sm">{projectIcon}</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-cyan-400/10 flex items-center justify-center">
              <Grid className="w-3 h-3 text-cyan-400/60" />
            </div>
            <div className="w-6 h-6 rounded-lg bg-cyan-400/10 flex items-center justify-center">
              <CheckSquare className="w-3 h-3 text-cyan-400/60" />
            </div>
            <div className="w-6 h-6 rounded-lg bg-cyan-400/10 flex items-center justify-center">
              <FileText className="w-3 h-3 text-cyan-400/60" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectContextPanel;
